import { ArrowLeftCircleIcon, ArrowUpCircleIcon, CheckCircleIcon, CheckIcon, CurrencyDollarIcon, DevicePhoneMobileIcon, HomeIcon, HomeModernIcon, MapIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../assets/loaders/imageLoader";
import { Alert } from "../../components/daisy";
import useScrollToTop from "../../hooks/useScrollToTop";
import { ModalContext } from "../../providers/ModalContext";
import { Paths } from "../../routes/paths";
import { RenderCardImage, renderCardInfo, RenderModalImage } from "./common";
import AnimateOnScroll from "../../components/common/miscellaneous/AnimateOnScroll";

const LopezJordanPage = () => {
	const navigate = useNavigate()
	const { showScrollTop, scrollToTop } = useScrollToTop({ threshold: 200 })

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [])

	return (
		<main id="unidades" className="min-h-screen bg-gradient-to-b from-gray-200 to-gray-210">
			<section className="relative h-[70vh] w-full" id="hero">
				<div className="absolute inset-0">
					<img src={images.backgroundImage3} alt="Background" className="w-full h-full object-cover" />
				</div>
				<div className="absolute inset-0 bg-black/40 flex items-center justify-center lg:justify-start lg:ml-16">
					<img src={images.logoCompleteDark} alt="Logo" className="absolute top-4 left-4 w-48 sm:w-80" />
					<div className="absolute bottom-[12vh] sm:left-8 text-center text-white">
						<h1 className="text-4xl md:text-5xl font-bold mb-4 font-mono uppercase">López Jordan al Río 2</h1>
						<p className="text-xl md:text-2xl max-w-2xl mx-auto font-mono">
							Naturaleza que te rodea
						</p>
					</div>
				</div>
			</section>

			<Features />
			<Financing />
			<ImagesGallery />
			<CTA />

			<AnimatePresence>
				{showScrollTop && (
					<>
						<motion.button
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							onClick={scrollToTop}
							className={`fixed right-6 bottom-4 z-50 p-2 rounded-full 
            bg-ghost text-primary-content shadow-lg hover:bg-neutral transition-colors duration-200`}
							aria-label="Scroll to top"
						>
							<ArrowUpCircleIcon className="w-8 text-info" title="Volver arriba" />
						</motion.button>
						<motion.button
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.8 }}
							onClick={() => navigate(Paths.home)}
							className={`fixed left-6 bottom-4 z-50 p-2 rounded-full 
            bg-ghost text-primary-content shadow-lg hover:bg-neutral transition-colors duration-200`}
							aria-label="Scroll to top"
						>
							<ArrowLeftCircleIcon className="w-8 text-secondary" title="Volver atrás" />
						</motion.button>
					</>
				)}
			</AnimatePresence>
		</main>
	);
};

const Features = () => {
	return (
		<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
			<section className="px-4 md:px-12 py-8 md:py-12" id="caracteristicas">
				<h2 className="text-2xl md:text-3xl font-bold mb-8 border-b pb-2 border-gray-900">Características</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
					{renderCardInfo({
						icon: <HomeIcon className="w-6 mr-2 flex-shrink-0" />,
						title: "Ubicación",
						description: <>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Condominio con servicios esenciales.
							</li>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Situado en Gdor Lopez Jordan casi esquina Pintor Nux.
							</li>
						</>
					})}
					{renderCardInfo({
						icon: <ShieldCheckIcon className="w-6 mr-2 flex-shrink-0" />,
						title: "Garantía de Calidad",
						description: <>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Construido con los más altos estándares y materiales duraderos.
							</li>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Inversión asegurada.
							</li>
						</>
					})}
					{renderCardInfo({
						icon: <HomeModernIcon className="w-6 mr-2 flex-shrink-0" />,
						title: "Arquitectura",
						description: <>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Proyectos estándar y a medida.
							</li>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Posibilidad de ampliación.
							</li>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Asesoramiento arquitectonico y seguimiento.
							</li>
						</>
					})}
					{renderCardInfo({
						icon: <CurrencyDollarIcon className="w-6 mr-2 flex-shrink-0" />,
						title: "Financiamiento",
						description: <>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Planes de financiamiento accesibles.
							</li>
							<li className="flex items-center gap-1">
								<CheckIcon className="w-6 mr-2 flex-shrink-0 text-success" />
								Posibilidad de financiar tu casa en 130 cuotas.
							</li>
						</>
					})}
				</div>
			</section>
		</AnimateOnScroll>
	)
}

const Financing = () => {
	return (
		<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
			<section className="bg-white px-4 md:px-12 py-8 md:py-12" id="financing">
				<h2 className="text-2xl md:text-3xl font-bold mb-8 border-b border-gray-900 pb-2">Cómo ingresar</h2>
				<div className="space-y-6">
					<div className="flex gap-4">
						<div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center font-bold">
							1
						</div>
						<div>
							<div className="flex items-center gap-1">
								<div>
									<span className="sm:text-lg font-semibold">Ingresá</span> al fideicomiso con un {" "}
									<span className="sm:text-lg font-semibold">Plan de 130 cuotas mensuales</span>.
								</div>
							</div>
						</div>
					</div>
					<div className="flex gap-4">
						<div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center font-bold">
							2
						</div>
						<div>
							<p className="">
								<span className="sm:text-lg font-semibold">Licitá</span> a partir de la cuota 12.
							</p>
						</div>
					</div>
					<div className="flex gap-4">
						<div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center font-bold">
							3
						</div>
						<div>
							<p className="">
								<span className="sm:text-lg font-semibold">Construcción y entrega</span> de la unidad.
							</p>
						</div>
					</div>
					<div className="flex gap-4">
						<div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center font-bold">
							4
						</div>
						<div>
							<p className="">
								<span className="sm:text-lg font-semibold">Continuás</span> pagando hasta completar el pago total.
							</p>
						</div>
					</div>
					<Alert className="flex gap-4">
						<CheckCircleIcon className="ml-1 w-6 flex-shrink-0 text-success" />
						<div>
							<p className="sm:text-lg">
								Actualización por el indice de la Camara Argentina de la Construcción (Indice CAC).
							</p>
						</div>
					</Alert>
				</div>
				{/* </div> */}
			</section >
		</AnimateOnScroll>
	)
}

const ImagesGallery = () => {
	const galleryImages = useMemo(() => [
		{ src: images.LopezJordan2, alt: 'lopez jordan 2' },
		{ src: images.LopezJordan1, alt: 'lopez jordan 1' },
		{ src: images.LopezJordan7, alt: 'lopez jordan 7' },
		{ src: images.LopezJordan4, alt: 'lopez jordan 4' },
		{ src: images.LopezJordan8, alt: 'lopez jordan 8' },
		{ src: images.LopezJordan9, alt: 'lopez jordan 9' },
		{ src: images.LopezJordan5, alt: 'lopez jordan 5' },
		{ src: images.LopezJordan6, alt: 'lopez jordan 6' },
		{ src: images.LopezJordan10, alt: 'lopez jordan 10' },
		{ src: images.LopezJordan12, alt: 'lopez jordan 12' },
		{ src: images.LopezJordan13, alt: 'lopez jordan 13' },
		{ src: images.LopezJordan14, alt: 'lopez jordan 14' },
		{ src: images.LopezJordan15, alt: 'lopez jordan 15' },
		{ src: images.LopezJordan16, alt: 'lopez jordan 16' },
	], []);

	const { openModal, closeModal } = useContext(ModalContext);

	const handleImageClick = useCallback((e: React.MouseEvent, imageSrc: string) => {
		openModal(<RenderModalImage images={galleryImages} initialImage={imageSrc} closeModal={closeModal} />,
			"p-0 bg-transparent shadow-none w-full max-w-full h-auto");
	}, [openModal, RenderModalImage]);
	return (
		<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
			<section className="px-4 md:px-12 py-8 md:py-12" id="galeria">
				<h2 className="text-2xl md:text-3xl font-bold mb-8 border-b pb-2 border-gray-900">Galería de Imágenes</h2>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{galleryImages.map((img, index) => (
						<RenderCardImage
							key={'image-' + index}
							image={img.src}
							alt={img.alt}
							onImageClick={(e) => handleImageClick(e, img.src)}
						/>
					))}
				</div>
			</section>
		</AnimateOnScroll>
	)
}

const CTA = () => {
	return (
		<AnimateOnScroll animationClass="fade-in-up" threshold={0.1} className="duration-1000">
			<section className="px-4 md:px-12 py-8 md:py-12" id="cta">
				<div className="max-w-3xl mx-auto p-8 bg-base-200 rounded-xl shadow-lg">
					<h2 className="text-2xl font-bold mb-4">¿Interesado en conocer más?</h2>
					<p className="mb-6">
						Contáctenos. Queremos escucharte.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<button
							className="btn btn-outline flex items-center"
							onClick={() => window.open(`https://wa.me/5493433022876?text=Hola, quiero contactarme por el proyecto López Jordan al Río.`)}
						>
							<DevicePhoneMobileIcon className="w-6 mr-2 flex-shrink-0" />
							Contactános
						</button>
						<button className="btn btn-outline" onClick={() => window.open("https://maps.app.goo.gl/qg6cDNu9EbSjLBfM7", '_blank')}>
							<MapIcon className="w-6 mr-2 flex-shrink-0" />
							Ubicación
						</button>
					</div>
				</div>
			</section>
		</AnimateOnScroll >
	)
}
export default LopezJordanPage;
